import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import { glow, lumpPath } from './env-kit';
import type { ProceduralDef } from '../types';

/**
 * Advance-room + ambience assets (docs/04): the gate shield strip and its
 * pylons (white — tinted per theme), ambient motes, the acid / rift burn
 * pools and the objective arrow with its target icons.
 */

/** Tileable shield strip (scrolls along the gate). White → tinted. */
export const envGateField: ProceduralDef = {
  key: 'env/gate-field', width: 128, height: 64, anchorX: 0.5, anchorY: 0.5, frames: 1,
  draw(ctx, w, h) {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, 'rgba(255, 255, 255, 0)');
    g.addColorStop(0.35, 'rgba(255, 255, 255, 0.55)');
    g.addColorStop(0.5, 'rgba(255, 255, 255, 0.95)');
    g.addColorStop(0.65, 'rgba(255, 255, 255, 0.55)');
    g.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // Hatched chevrons — reads as "barrier" (seamless across x)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 6;
    for (let x = -32; x <= w + 32; x += 32) {
      ctx.beginPath();
      ctx.moveTo(x, h * 0.22);
      ctx.lineTo(x + 14, h * 0.5);
      ctx.lineTo(x, h * 0.78);
      ctx.stroke();
    }
  },
};

/** Gate pylon (jamb + lamp). Light grey/white → takes the theme tint. */
export const envGatePylon: ProceduralDef = {
  key: 'env/gate-pylon', width: 96, height: 96, anchorX: 0.5, anchorY: 0.5, frames: 1,
  draw(ctx, w) {
    const c = w / 2;
    ctx.beginPath();
    ctx.roundRect(c - 26, c - 26, 52, 52, 12);
    fillOutlined(ctx, 0xb8bcc8, 6);
    ctx.beginPath();
    ctx.roundRect(c - 16, c - 16, 32, 32, 8);
    fillOutlined(ctx, 0x8a8e9c, 4);
    glow(ctx, c, c, 20, '255, 255, 255', 0.95);
    circle(ctx, c, c, 7);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
  },
};

/** Ambient motes: 0 spark streak · 1 ember · 2 spore · 3 glitch diamond (white). */
export const envMote: ProceduralDef = {
  key: 'env/mote', width: 32, height: 32, anchorX: 0.5, anchorY: 0.5, frames: 4,
  draw(ctx, w, _h, frame) {
    const c = w / 2;
    if (frame === 0) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(c, c - 7); ctx.lineTo(c, c + 7); ctx.stroke();
      glow(ctx, c, c, 6, '255, 255, 255', 0.6);
    } else if (frame === 1) {
      glow(ctx, c, c, 8, '255, 255, 255', 0.9);
      circle(ctx, c, c, 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
    } else if (frame === 2) {
      glow(ctx, c, c, 10, '255, 255, 255', 0.45);
      circle(ctx, c, c, 4);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    } else {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      ctx.moveTo(c, c - 5); ctx.lineTo(c + 4, c); ctx.lineTo(c, c + 5); ctx.lineTo(c - 4, c);
      ctx.closePath();
      ctx.fill();
    }
  },
};

/** Burn pool family (2 bubble frames): same mechanic as lava, themed look. */
function makeBurnPool(key: string, rim: number, core: readonly [string, string, string], bubble: string): ProceduralDef {
  return {
    key, width: 192, height: 192, anchorX: 0.5, anchorY: 0.5, frames: 2,
    draw(ctx, w, _h, frame) {
      const c = w / 2;
      const R = c - 8;
      lumpPath(ctx, c, c, R, 10, 0.07, frame);
      fillOutlined(ctx, rim, 6);
      const g = ctx.createRadialGradient(c, c, R * 0.12, c, c, R * 0.88);
      g.addColorStop(0, core[0]);
      g.addColorStop(0.45, core[1]);
      g.addColorStop(1, core[2]);
      circle(ctx, c, c, R * 0.86);
      ctx.fillStyle = g;
      ctx.fill();
      ctx.fillStyle = bubble;
      const bubbles = frame === 0
        ? [[-0.3, -0.15, 7], [0.25, 0.2, 5], [-0.05, 0.42, 4]] as const
        : [[0.32, -0.25, 6], [-0.22, 0.3, 5], [0.05, -0.05, 4]] as const;
      for (const [bx, by, br] of bubbles) {
        circle(ctx, c + bx * R, c + by * R, br);
        ctx.fill();
      }
    },
  };
}

export const envAcidPool = makeBurnPool(
  'env/acid-pool', 0x26361e,
  ['rgba(235, 255, 170, 1)', 'rgba(140, 230, 60, 1)', 'rgba(50, 120, 30, 0.95)'],
  'rgba(230, 255, 190, 0.9)',
);
export const envRiftPool = makeBurnPool(
  'env/rift-pool', 0x1a1433,
  ['rgba(255, 235, 255, 1)', 'rgba(190, 110, 255, 1)', 'rgba(70, 30, 140, 0.95)'],
  'rgba(245, 225, 255, 0.9)',
);

/**
 * Objective arrow (ui): 0 chevron arrow (points +x, white → tinted) ·
 * 1 boss crest · 2 gate · 3 enemy · 4 portal — icon first, no words.
 */
export const uiObjectiveArrow: ProceduralDef = {
  key: 'ui/objective-arrow', width: 96, height: 96, anchorX: 0.5, anchorY: 0.5, frames: 5,
  draw(ctx, w, _h, frame) {
    const c = w / 2;
    ctx.lineJoin = 'round';
    if (frame === 0) {
      ctx.beginPath();
      ctx.moveTo(c + 34, c);
      ctx.lineTo(c - 6, c - 30);
      ctx.lineTo(c - 6, c - 12);
      ctx.lineTo(c - 30, c - 12);
      ctx.lineTo(c - 30, c + 12);
      ctx.lineTo(c - 6, c + 12);
      ctx.lineTo(c - 6, c + 30);
      ctx.closePath();
      fillOutlined(ctx, 0xffffff, 6);
      return;
    }
    circle(ctx, c, c, 30);
    fillOutlined(ctx, COLORS.outline, 4);
    if (frame === 1) {
      // Horned crest
      ctx.beginPath();
      ctx.moveTo(c - 20, c - 18); ctx.lineTo(c - 8, c - 4); ctx.lineTo(c, c - 12);
      ctx.lineTo(c + 8, c - 4); ctx.lineTo(c + 20, c - 18); ctx.lineTo(c + 16, c + 10);
      ctx.lineTo(c, c + 20); ctx.lineTo(c - 16, c + 10);
      ctx.closePath();
      fillOutlined(ctx, COLORS.dangerRed, 3);
      circle(ctx, c - 6, c + 4, 3); circle(ctx, c + 6, c + 4, 3);
      ctx.fillStyle = '#fff4d8';
      ctx.fill();
    } else if (frame === 2) {
      // Gate: two pylons + open chevron
      ctx.fillStyle = css(0xf0ead6);
      ctx.fillRect(c - 20, c - 16, 7, 32);
      ctx.fillRect(c + 13, c - 16, 7, 32);
      ctx.strokeStyle = css(COLORS.playerCyan);
      ctx.lineWidth = 5;
      ctx.beginPath(); ctx.moveTo(c - 5, c - 9); ctx.lineTo(c + 4, c); ctx.lineTo(c - 5, c + 9); ctx.stroke();
    } else if (frame === 3) {
      circle(ctx, c, c, 13);
      fillOutlined(ctx, COLORS.dangerOrange, 3);
      circle(ctx, c, c, 5);
      ctx.fillStyle = css(shade(COLORS.dangerOrange, 0.5));
      ctx.fill();
    } else {
      for (let r = 18; r > 4; r -= 6) {
        circle(ctx, c, c, r);
        ctx.strokeStyle = r === 18 ? css(COLORS.playerCyan) : 'rgba(235, 245, 255, 0.9)';
        ctx.lineWidth = 4;
        ctx.stroke();
      }
    }
  },
};
